<template>
  <div class="result-edit-form">
    <fieldset class="result-edit-form__group">
      <legend>Program</legend>
      <label class="visually-hidden" :for="degreeId">Degree type</label>
      <TypeAheadSelect
        :id="degreeId"
        :model-value="selectedDegree"
        :options="degreeOptions"
        data-testid="edit-degree-type"
        placeholder="select a degree type"
        aria-label="Degree type"
        :aria-describedby="describedBy('degreeType')"
        :aria-invalid="Boolean(errors.degreeType)"
        class="mb-space-xxs"
        @changed="onDegreeChanged"
      />
      <p
        v-if="errors.degreeType"
        :id="errorId('degreeType')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.degreeType }}</span>
      </p>
      <label class="visually-hidden" :for="programId">Program</label>
      <TypeAheadSelect
        :id="programId"
        :model-value="selectedProgram"
        :options="programOptions"
        :disabled="!modelValue.degreeType"
        data-testid="edit-program"
        placeholder="select a program"
        aria-label="Program"
        :aria-describedby="describedBy('program')"
        :aria-invalid="Boolean(errors.program)"
        @changed="onProgramChanged"
      />
      <p
        v-if="errors.program"
        :id="errorId('program')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.program }}</span>
      </p>
    </fieldset>

    <fieldset v-if="!isCertificate" class="result-edit-form__group">
      <legend>Student type</legend>
      <label class="visually-hidden" :for="creditOptionId">Student type</label>
      <TypeAheadSelect
        :id="creditOptionId"
        :model-value="selectedCreditOption"
        :options="creditOptions"
        :disabled="!modelValue.program"
        data-testid="edit-credit-option"
        placeholder="select enrollment status"
        aria-label="Student type"
        :aria-describedby="describedBy('creditsPerSemester')"
        :aria-invalid="Boolean(errors.creditsPerSemester)"
        @changed="onCreditOptionChanged"
      />
      <p
        class="result-edit-form__derived border border-light-4 bg-light-1"
        data-testid="edit-credits-derived"
      >
        <svg
          class="result-edit-form__derived-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="12"
          viewBox="0 0 16 12"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M1.33333 4.3875L7.14444 6.69375C7.41667 6.80089 7.70556 6.85714 8 6.85714C8.29444 6.85714 8.58333 6.80089 8.85556 6.69375L15.5889 4.02054C15.8389 3.92143 16 3.68839 16 3.42857C16 3.16875 15.8389 2.93571 15.5889 2.83661L8.85556 0.163393C8.58333 0.05625 8.29444 0 8 0C7.70556 0 7.41667 0.05625 7.14444 0.163393L0.411111 2.83661C0.161111 2.93571 0 3.16875 0 3.42857V11.3571C0 11.7134 0.297222 12 0.666667 12C1.03611 12 1.33333 11.7134 1.33333 11.3571V4.3875ZM2.66667 6.30804V9.42857C2.66667 10.8482 5.05556 12 8 12C10.9444 12 13.3333 10.8482 13.3333 9.42857V6.30536L9.36389 7.88304C8.93056 8.05446 8.46944 8.14286 8 8.14286C7.53056 8.14286 7.06944 8.05446 6.63611 7.88304L2.66667 6.30536V6.30804Z"
            fill="#747474"
          />
        </svg>
        <span>{{ modelValue.creditsPerSemester }} credits</span>
      </p>
      <p
        v-if="errors.creditsPerSemester"
        :id="errorId('creditsPerSemester')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.creditsPerSemester }}</span>
      </p>
    </fieldset>

    <CreditInputModal
      :visible="manualModalVisible"
      :model-value="modelValue.creditsPerSemester"
      :degree-type="modelValue.degreeType"
      :warning-message="content.creditsWarningMessage || ''"
      @confirm="onManualConfirm"
      @close="onManualClose"
      @close-cross="emit('credit-modal-close-cross')"
      @cancel="emit('credit-modal-cancel')"
      @credits-change="emit('credits-change', $event)"
    />

    <fieldset v-if="showResidency" class="result-edit-form__group">
      <legend>Arizona resident?</legend>
      <div
        v-if="residencyDescription"
        data-testid="edit-residency-description"
        role="presentation"
        @click="handleFaqLink"
        v-html="residencyDescription"
      ></div>
      <label class="visually-hidden" :for="residencyId">Arizona residency status</label>
      <TypeAheadSelect
        :id="residencyId"
        :model-value="selectedResidency"
        :options="residencyOptions"
        data-testid="edit-residency"
        placeholder="select"
        aria-label="Arizona residency status"
        :aria-describedby="describedBy('residencyStatus')"
        :aria-invalid="Boolean(errors.residencyStatus)"
        @changed="onResidencyChanged"
      />
      <p
        v-if="errors.residencyStatus"
        :id="errorId('residencyStatus')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.residencyStatus }}</span>
      </p>
    </fieldset>

    <div v-if="showTransferCredits" class="result-edit-form__field">
      <label :for="transferId">{{ content.transferCreditsTitle || "Transfer credits" }}</label>
      <div class="result-edit-form__input">
        <CurrencyInput
          :id="transferId"
          :model-value="modelValue.transferCredits"
          aria-label="Transfer credits"
          :aria-describedby="describedBy('transferCredits')"
          :aria-invalid="Boolean(errors.transferCredits)"
          class="form-control result-edit-form__number"
          data-testid="edit-transfer-credits"
          suffix="credits"
          whole-number
          @update:model-value="onTransferCreditsInput"
          @commit="emit('field-commit', 'transferCredits', $event)"
        />
      </div>
      <p
        v-if="errors.transferCredits"
        :id="errorId('transferCredits')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        tabindex="0"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.transferCredits }}</span>
      </p>
    </div>

    <div v-if="showGrants" class="result-edit-form__field">
      <label :for="grantsId">{{ content.grantsTitle || "Grants" }}</label>
      <div class="result-edit-form__input">
        <CurrencyInput
          :id="grantsId"
          prefix="$"
          suffix="/year"
          whole-number
          class="form-control result-edit-form__number"
          data-testid="edit-grants"
          :model-value="modelValue.grants"
          :aria-describedby="describedBy('grants')"
          :aria-invalid="Boolean(errors.grants)"
          @update:model-value="onFinancialAidInput('grants', $event)"
          @commit="emit('field-commit', 'grants', $event)"
        />
      </div>
      <p
        v-if="errors.grants"
        :id="errorId('grants')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.grants }}</span>
      </p>
    </div>

    <div v-if="!isCertificate" class="result-edit-form__field">
      <label :for="scholarshipsId">{{ content.schorlarshipsTitle || "Scholarships" }}</label>
      <div class="result-edit-form__input">
        <CurrencyInput
          :id="scholarshipsId"
          prefix="$"
          suffix="/year"
          whole-number
          class="form-control result-edit-form__number"
          data-testid="edit-scholarships"
          :model-value="modelValue.scholarships"
          :aria-describedby="describedBy('scholarships')"
          :aria-invalid="Boolean(errors.scholarships)"
          @update:model-value="onFinancialAidInput('scholarships', $event)"
          @commit="emit('field-commit', 'scholarships', $event)"
        />
      </div>
      <p
        v-if="errors.scholarships"
        :id="errorId('scholarships')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.scholarships }}</span>
      </p>
    </div>

    <div v-if="!isCertificate" class="result-edit-form__field">
      <label :for="employerId">{{
        content.employerBenefitTitle || "Employer tuition benefit"
      }}</label>
      <div class="result-edit-form__input">
        <CurrencyInput
          :id="employerId"
          prefix="$"
          suffix="/year"
          whole-number
          class="form-control result-edit-form__number"
          data-testid="edit-employer-benefit"
          :model-value="modelValue.employerTuitionBenefit"
          :aria-describedby="describedBy('employerTuitionBenefit')"
          :aria-invalid="Boolean(errors.employerTuitionBenefit)"
          @update:model-value="onFinancialAidInput('employerTuitionBenefit', $event)"
          @commit="emit('field-commit', 'employerTuitionBenefit', $event)"
        />
      </div>
      <p
        v-if="errors.employerTuitionBenefit"
        :id="errorId('employerTuitionBenefit')"
        class="savings-banner"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningIcon class="savings-banner__icon" />
        <span class="savings-banner__message">{{ errors.employerTuitionBenefit }}</span>
      </p>
    </div>

    <Teleport to="body">
      <div
        v-if="faqConfirmVisible"
        class="result-edit-form__confirm-backdrop"
        data-testid="edit-faq-confirm-backdrop"
      >
        <div
          class="result-edit-form__confirm-dialog py-space-md px-space-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-faq-confirm-title"
          aria-describedby="edit-faq-confirm-description"
          data-testid="edit-faq-confirm-dialog"
        >
          <h3 id="edit-faq-confirm-title" class="h3-medium">Leave this page?</h3>
          <p id="edit-faq-confirm-description" class="m-0 pb-space-md">
            Your edits and estimate won't be saved if you continue. You can also finish your edits
            and scroll to the FAQ section below to learn more about out-of-state tuition.
          </p>
          <div class="result-edit-form__confirm-actions">
            <button
              ref="faqConfirmCancelButtonRef"
              type="button"
              class="btn btn-dark-3 result-edit-form__confirm-button"
              data-testid="edit-faq-confirm-cancel"
              @click="cancelFaqNavigation"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-outline-dark result-edit-form__confirm-button"
              data-testid="edit-faq-confirm-continue"
              @click="confirmFaqNavigation"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, nextTick, ref, watch } from "vue";

import TypeAheadSelect, { type OptionType } from "~/components/TypeAheadSelect.vue";
import CreditInputModal from "~/components/what-it-costs-page/cost-calculator-v2/CreditInputModal.vue";
import CurrencyInput from "~/components/what-it-costs-page/cost-calculator-v2/CurrencyInput.vue";
import type { DegreeSeekingProgram, WhatItCosts } from "~/server/resolvers/resolvers-types";
import {
  filterPrograms,
  getFullTimeCredits,
  residencyAffectsCost,
  shouldDisplayTransferCredits,
  validateDetails
} from "~/src/services/TuitionCalculatorFormService";
import type {
  FaqLinkTarget,
  TuitionCalculatorCreditOption,
  TuitionCalculatorDegreeType,
  TuitionCalculatorResidency,
  TuitionCalculatorSelections,
  TuitionCalculatorValidationErrors
} from "~/types/tuition-calculator";
import { isExternalUrl } from "~/utils";

const WarningIcon = defineComponent({
  name: "WarningIcon",
  setup(_, { attrs }) {
    return () =>
      h(
        "svg",
        {
          ...attrs,
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 16 16",
          "aria-hidden": "true",
          focusable: "false"
        },
        [
          h("path", {
            fill: "currentColor",
            d: "M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          })
        ]
      );
  }
});

const props = defineProps<{
  modelValue: TuitionCalculatorSelections;
  programs: DegreeSeekingProgram[];
  errors: TuitionCalculatorValidationErrors;
  exceptionProgramCodes: string[];
  content: Partial<WhatItCosts>;
}>();

const residencyDescription = computed(() =>
  (props.content.residencyStatusDescriptionMobile ?? "").replace(
    /<a\b[^>]*>[\s\S]*?<\/a>\s*[.,;:!?]?/gi,
    (link) => `<span class="result-edit-form__residency-link">${link}</span>`
  )
);

const faqConfirmVisible = ref(false);
const pendingFaqTarget = ref<FaqLinkTarget | null>(null);
const faqConfirmCancelButtonRef = ref<HTMLButtonElement | null>(null);

const emit = defineEmits<{
  (event: "update:modelValue", value: TuitionCalculatorSelections): void;
  (event: "selection", field: string, text: string): void;
  (event: "credit-modal-open" | "credit-modal-close-cross" | "credit-modal-cancel"): void;
  (event: "credit-modal-confirm" | "credits-change", value: number): void;
  (event: "field-commit", field: MoneyField, value: number): void;
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const handleFaqLink = (event: MouseEvent): void => {
  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor || isExternalUrl(anchor.getAttribute("href") ?? "")) return;
  event.preventDefault();

  if (typeof window !== "undefined" && window.innerWidth < 992) {
    pendingFaqTarget.value = "residency";
    faqConfirmVisible.value = true;
    void nextTick(() => faqConfirmCancelButtonRef.value?.focus({ preventScroll: true }));
    return;
  }

  emit("faq-link", "residency");
};

const cancelFaqNavigation = (): void => {
  pendingFaqTarget.value = null;
  faqConfirmVisible.value = false;
};

const confirmFaqNavigation = (): void => {
  const target = pendingFaqTarget.value;
  pendingFaqTarget.value = null;
  faqConfirmVisible.value = false;
  if (target) emit("faq-link", target);
};

type MoneyField = "transferCredits" | "grants" | "scholarships" | "employerTuitionBenefit";

const degreeId = "tuition-edit-degree-type";
const programId = "tuition-edit-program";
const creditOptionId = "tuition-edit-credit-option";
const residencyId = "tuition-edit-residency";
const transferId = "tuition-edit-transfer-credits";
const grantsId = "tuition-edit-grants";
const scholarshipsId = "tuition-edit-scholarships";
const employerId = "tuition-edit-employer-benefit";

const degreeOptions: OptionType[] = [
  { value: "Undergraduate", text: "Undergraduate" },
  { value: "Graduate", text: "Graduate" },
  { value: "Certificates", text: "Graduate certificate" }
];
const creditOptions: OptionType[] = [
  { value: "part-time", text: "Part-time student" },
  { value: "full-time", text: "Full-time student" },
  { value: "manual", text: "Enter credits" }
];
const residencyOptions: OptionType[] = [
  { value: "res", text: "Yes" },
  { value: "nores", text: "No" }
];

const programOptions = computed<OptionType[]>(() =>
  props.modelValue.degreeType
    ? filterPrograms(props.programs, props.modelValue.degreeType).map((p) => ({
        value: String(p.id),
        text: p.title ?? ""
      }))
    : []
);

const selectedDegree = computed<OptionType | null>(() =>
  props.modelValue.degreeType
    ? (degreeOptions.find((option) => option.value === props.modelValue.degreeType) ?? null)
    : null
);
const selectedProgram = computed<OptionType | null>(() =>
  props.modelValue.program
    ? {
        value: String(props.modelValue.program.id),
        text: props.modelValue.program.title ?? ""
      }
    : null
);
const selectedCreditOption = computed<OptionType | null>(() =>
  props.modelValue.creditOption
    ? (creditOptions.find((option) => option.value === props.modelValue.creditOption) ?? null)
    : null
);
const selectedResidency = computed<OptionType | null>(() =>
  props.modelValue.residencyStatus
    ? (residencyOptions.find((option) => option.value === props.modelValue.residencyStatus) ?? null)
    : null
);

const isCertificate = computed(() => props.modelValue.degreeType === "Certificates");
const showGrants = computed(
  () => props.modelValue.degreeType !== "Graduate" && props.modelValue.degreeType !== "Certificates"
);
const showTransferCredits = computed(() => shouldDisplayTransferCredits(props.modelValue));
const showResidency = computed(() =>
  Boolean(
    props.modelValue.degreeType &&
    props.modelValue.degreeType !== "Certificates" &&
    (residencyAffectsCost(props.modelValue) ||
      (!props.modelValue.program &&
        Number.isInteger(props.modelValue.creditsPerSemester) &&
        props.modelValue.creditsPerSemester > 11)) &&
    !validateDetails(props.modelValue).creditsPerSemester
  )
);

const errorId = (field: string): string => `tuition-edit-${field}-error`;
const describedBy = (field: string): string | undefined => {
  return props.errors[field as keyof TuitionCalculatorValidationErrors]
    ? errorId(field)
    : undefined;
};
const update = (patch: Partial<TuitionCalculatorSelections>): void =>
  emit("update:modelValue", { ...props.modelValue, ...patch });

watch(
  () =>
    [
      props.modelValue.degreeType,
      props.modelValue.program?.displayTransferCredits,
      props.modelValue.transferCredits
    ] as const,
  ([_degreeType, _displayTransferCredits, transferCredits]) => {
    if (!showTransferCredits.value && transferCredits !== 0) {
      update({ transferCredits: 0 });
    }
  }
);

const manualModalVisible = ref(false);
type ManualSelectionSnapshot = Pick<
  TuitionCalculatorSelections,
  "creditOption" | "creditsPerSemester"
>;
const manualSelectionBeforeOpen = ref<ManualSelectionSnapshot | null>(null);

const onDegreeChanged = (option: OptionType | null): void => {
  const degreeType = (option?.value ?? null) as TuitionCalculatorDegreeType | null;
  if (
    degreeType === props.modelValue.degreeType &&
    props.modelValue.program === null &&
    props.modelValue.creditOption === null &&
    props.modelValue.creditsPerSemester === 0 &&
    props.modelValue.residencyStatus === null
  )
    return;
  manualSelectionBeforeOpen.value = null;
  update({
    degreeType,
    program: null,
    creditOption: null,
    creditsPerSemester: 0,
    residencyStatus: null
  });
  emit(
    "selection",
    "degreeType",
    degreeOptions.find((option) => option.value === degreeType)?.text ?? ""
  );
};
const onProgramChanged = (option: OptionType | null): void => {
  const program = option?.value
    ? (props.programs.find((p) => String(p.id) === option.value) ?? null)
    : null;
  // TypeAheadSelect can emit the previous selection while its options are
  // being replaced after a degree change. Never put a program from a
  // different degree back into the edit draft.
  if (program && program.category?.title !== props.modelValue.degreeType) return;
  const nextResidency = program?.costOverride ? null : props.modelValue.residencyStatus;
  if (
    (program?.id ?? null) === (props.modelValue.program?.id ?? null) &&
    nextResidency === props.modelValue.residencyStatus
  )
    return;
  manualSelectionBeforeOpen.value = null;
  update({
    program,
    residencyStatus: nextResidency
  });
  emit("selection", "program", program?.title ?? "");
};
const onCreditOptionChanged = (option: OptionType | null): void => {
  const creditOption = (option?.value ?? null) as TuitionCalculatorCreditOption | null;
  if (!creditOption) {
    if (props.modelValue.creditOption === null && props.modelValue.creditsPerSemester === 0) return;
    manualSelectionBeforeOpen.value = null;
    update({ creditOption: null, creditsPerSemester: 0 });
    emit("selection", "creditOption", "");
    return;
  }
  if (creditOption === "manual") {
    if (manualModalVisible.value && props.modelValue.creditOption === "manual") return;
    manualSelectionBeforeOpen.value = {
      creditOption: props.modelValue.creditOption,
      creditsPerSemester: props.modelValue.creditsPerSemester
    };
    update({ creditOption, creditsPerSemester: 0 });
    emit("credit-modal-open");
    manualModalVisible.value = true;
    return;
  }
  if (!props.modelValue.degreeType) return;
  const creditsPerSemester =
    creditOption === "part-time" ? 6 : getFullTimeCredits(props.modelValue.degreeType);
  if (
    props.modelValue.creditOption === creditOption &&
    props.modelValue.creditsPerSemester === creditsPerSemester
  )
    return;
  update({ creditOption, creditsPerSemester });
  emit(
    "selection",
    "creditOption",
    creditOptions.find((option) => option.value === creditOption)?.text ?? ""
  );
};
const onManualConfirm = (creditsPerSemester: number): void => {
  manualSelectionBeforeOpen.value = null;
  update({ creditOption: "manual", creditsPerSemester });
  emit("credit-modal-confirm", creditsPerSemester);
  void closeManualModal(false);
};
const closeManualModal = async (restoreSelection: boolean): Promise<void> => {
  if (restoreSelection && manualSelectionBeforeOpen.value) {
    update(manualSelectionBeforeOpen.value);
  }
  manualSelectionBeforeOpen.value = null;
  manualModalVisible.value = false;
  await nextTick();
  document.getElementById("tuition-edit-credit-option_typeahead__search")?.focus();
};
const onManualClose = (): void => {
  void closeManualModal(true);
};
const onResidencyChanged = (option: OptionType | null): void => {
  const residencyStatus = (option?.value ?? null) as TuitionCalculatorResidency | null;
  if (residencyStatus === props.modelValue.residencyStatus) return;
  update({ residencyStatus });
  emit(
    "selection",
    "residencyStatus",
    residencyOptions.find((item) => item.value === residencyStatus)?.text ?? ""
  );
};
const onTransferCreditsInput = (value: number): void => {
  update({ transferCredits: value });
};

const onFinancialAidInput = (
  field: Exclude<MoneyField, "transferCredits">,
  value: number
): void => {
  update({ [field]: value });
};
</script>

<style scoped lang="scss">
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
.result-edit-form__group {
  margin: 0 0 1.25rem;
  padding: 0;
  border: 0;
}
.result-edit-form__group legend {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  padding: 0;
}
.result-edit-form__field {
  margin-bottom: 1.25rem;
}
.result-edit-form__field label {
  display: block;
  font-weight: 700;
  margin-bottom: 0.35rem;
}
.result-edit-form__input {
  position: relative;
  display: flex;
  align-items: center;
}
.result-edit-form__input--prefix .result-edit-form__unit {
  position: absolute;
  left: 0.6rem;
}
.result-edit-form__input--suffix .result-edit-form__unit {
  position: absolute;
  right: 0.6rem;
}
.result-edit-form__number {
  width: 100%;
}
.result-edit-form__derived {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 3rem;
  margin: 0;
  padding: 0.75rem;
  border: 1px solid var(--rds-light-4, #d0d0d0);
  border-top: 0;
  background: var(--rds-light-1, #fafafa);
  color: var(--rds-dark-2, #747474);
}
.result-edit-form__derived-icon {
  flex: 0 0 auto;
}

.result-edit-form :deep(.result-edit-form__residency-link) {
  display: inline;
}

.result-edit-form__confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(25, 25, 25, 0.58);
}

.result-edit-form__confirm-dialog {
  width: min(100%, 21rem);
  border-radius: 0;
  background: #fff;
  color: var(--rds-dark-3, #191919);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.24);
}

.result-edit-form__confirm-dialog h3 {
  margin: 0 0 0.75rem;
  font-weight: 700;
  line-height: 1.35;
}

.result-edit-form__confirm-dialog p {
  margin: 0 0 1.25rem;
  color: var(--rds-dark-2, #747474);
  font-size: 0.875rem;
  line-height: 1.55;
}

.result-edit-form__confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.result-edit-form__confirm-button {
  width: 100%;
  border-radius: 999px;
  font-weight: 700;
}
</style>
